import {
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
} from "@angular/core"
import { Router, RouterModule } from "@angular/router"
import { MatIconModule } from "@angular/material/icon"
import { DatePipe, DecimalPipe } from "@angular/common"
import { FullscreenControl, NavigationControl } from "maplibre-gl"
import { AppBarComponent } from "../../../shared/components/app-bar/app-bar.component"
import { MainContentComponent } from "../../../shared/components/main-content/main-content.component"
import { SeoComponent } from "../../../shared/components/seo/seo.component"
import { ERoutes } from "../../../shared/constants/routes.enum"
import { EMeasurementStatus } from "../../../test/constants/measurement-status.enum"
import {
  ClassificationService,
  THRESHOLD_DOWNLOAD,
  THRESHOLD_PING,
  THRESHOLD_UPLOAD,
} from "../../../shared/services/classification.service"
import { MainStore } from "../../../shared/store/main.store"
import { catchError, finalize, of, switchMap, takeUntil } from "rxjs"
import { toObservable } from "@angular/core/rxjs-interop"
import {
  RecommendationUseCase,
  INTERNET_ACTIVITIES,
  SpotRecommendationService,
} from "../../../spots/services/spot-recommendation.service"
import { ISpotRecommendation } from "../../../spots/interfaces/spot-recommendation.interface"
import { HubService } from "../../../hub/services/hub.service"
import { IAnnouncement } from "../../../hub/interfaces/hub.interface"
import { InsightService } from "../../../insight/services/insight.service"
import { IPersonalInsightTest } from "../../../insight/interfaces/insight.interface"
import {
  RMBTTermsV6,
  TERMS_VERSION,
  UUID,
} from "../../../test/constants/strings"
import { HistoryRepositoryService } from "../../../history/repository/history-repository.service"
import { IQoeItem } from "../../../history/interfaces/qoe-item.interface"
import Cookies from "js-cookie"

type QualityLabel = "Excellent" | "Good" | "Fair" | "Poor" | "Low data"

interface NetworkQuality {
  score: number
  label: QualityLabel
  color: string
  bgColor: string
  updatedAgo: string
  download: number
  upload: number
  latency: number | null
}

interface ActivityRating {
  activity: string
  rating: QualityLabel
  color: string
}

interface BestSpot {
  id: number
  label: string
  place: string
  description: string
  quality: QualityLabel
}

@Component({
  selector: "app-home",
  imports: [
    RouterModule,
    DatePipe,
    DecimalPipe,
    MatIconModule,
    AppBarComponent,
    MainContentComponent,
  ],
  templateUrl: "./home.component.html",
  styleUrl: "./home.component.scss",
})
export class HomeComponent
  extends SeoComponent
  implements OnDestroy
{
  private readonly router = inject(Router)
  private readonly mainStore = inject(MainStore)
  private readonly spotRecommendations = inject(SpotRecommendationService)
  private readonly hubService = inject(HubService)
  private readonly insightService = inject(InsightService)
  private readonly historyRepository = inject(HistoryRepositoryService)
  private qoeRequestVersion = 0

  readonly eRoutes = ERoutes
  readonly mapContainerId = "homeMapPreview"
  readonly mapControls = [new NavigationControl(), new FullscreenControl()]

  /** Toggle between welcome empty state and dashboard with results. */
  readonly hasTestResults = signal(false)

  readonly quality = signal<NetworkQuality>({
    score: 0,
    label: "Poor",
    color: "var(--color-text-disabled)",
    bgColor: "var(--color-surface-variant)",
    updatedAgo: "never",
    download: 0,
    upload: 0,
    latency: null,
  })

  readonly activityRatings = signal<ActivityRating[]>([])

  readonly bestSpots = signal<BestSpot[]>([])
  readonly spotsLoading = signal(true)
  readonly spotsError = signal(false)
  readonly selectedCategory = signal<RecommendationUseCase | null>(null)

  readonly filteredBestSpots = computed(() => this.bestSpots())

  readonly categoryFilters: {
    id: RecommendationUseCase | null
    label: string
  }[] = [
    { id: null, label: "All" },
    ...INTERNET_ACTIVITIES,
  ]

  readonly announcements = signal<IAnnouncement[]>([])
  readonly announcementsLoading = signal(true)
  readonly announcementsError = signal(false)

  readonly isRetesting = signal(false)
  readonly progress = signal(0)
  readonly testPhase = signal("Preparing test…")
  readonly testError = signal<string | null>(null)

  readonly recentTests = signal<IPersonalInsightTest[]>([])
  readonly recentTestsLoading = signal(false)
  readonly recentTestsError = signal(false)

  constructor() {
    super()
    this.title = "Home"
    this.loadLatestInsight()
    this.loadAnnouncements()
    this.mainStore.error$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error) => {
        if (error && this.isRetesting()) {
          this.failRetest(
            "The network test could not be completed. Please try again.",
          )
        }
      })
    toObservable(this.selectedCategory)
      .pipe(
        switchMap((category) => {
          this.spotsLoading.set(true)
          this.spotsError.set(false)
          return this.spotRecommendations
            .getInternetRecommendations(category ?? undefined)
            .pipe(
              catchError(() => {
                this.spotsError.set(true)
                return of([])
              }),
              finalize(() => this.spotsLoading.set(false)),
            )
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((spots) => {
        this.bestSpots.set(spots.slice(0, 4).map((spot) => this.toBestSpot(spot)))
      })
  }

  loadAnnouncements(): void {
    this.announcementsLoading.set(true)
    this.announcementsError.set(false)
    this.hubService
      .getAnnouncements(3)
      .pipe(
        catchError(() => {
          this.announcementsError.set(true)
          return of([])
        }),
        finalize(() => this.announcementsLoading.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((items) => this.announcements.set(items))
  }

  override ngOnDestroy(): void {
    this.qoeRequestVersion++
    super.ngOnDestroy()
    if (this.isRetesting()) {
      this.testService.sendAbort(this.testStore.basicNetworkInfo().testUuid)
      this.testService.stopUpdates()
      this.testStore.isRunning.set(false)
    }
  }

  selectCategory(id: RecommendationUseCase | null): void {
    this.selectedCategory.set(id)
  }

  runTest(): void {
    if (!this.hasAcceptedTerms()) {
      void this.router.navigate(
        [this.i18nStore.routeRoot, ERoutes.TERMS],
        {
          queryParams: {
            next: ERoutes.HOME,
            current: ERoutes.HOME,
          },
        },
      )
      return
    }
    this.runInlineTest()
  }

  private hasAcceptedTerms(): boolean {
    return Boolean(
      globalThis.localStorage?.getItem(TERMS_VERSION) ||
        Cookies.get(RMBTTermsV6),
    )
  }

  testButtonLabel(): string {
    if (this.isRetesting()) {
      return "Testing…"
    }
    if (this.testError()) {
      return "Try Again"
    }
    return this.hasTestResults() ? "Retest" : "Start Test"
  }

  private runInlineTest(): void {
    if (this.isRetesting()) {
      return
    }

    this.isRetesting.set(true)
    this.testStore.isRunning.set(true)
    this.progress.set(0)
    this.testPhase.set("Preparing test…")
    this.testError.set(null)

    void this.testService.triggerNextTest({
      referrer: globalThis.document?.referrer || "https://unknown.invalid",
      onStateUpdate: (state) => {
        this.progress.set(
          this.testService.getProgressSegment(state.phase, state.progress),
        )
        this.testPhase.set(this.phaseLabel(state.phase))

        if (state.phase === EMeasurementStatus.END) {
          this.finishRetest(state.down, state.up, state.ping, state.testUuid)
        } else if (
          state.phase === EMeasurementStatus.ERROR ||
          state.phase === EMeasurementStatus.ABORTED
        ) {
          this.failRetest("The network test could not be completed. Please try again.")
        }
      },
    }).catch(() => {
      this.failRetest("The network test could not be started. Please try again.")
    })
  }



  qualityClass(quality: QualityLabel): string {
    return quality.toLowerCase().replaceAll(" ", "")
  }

  routeLink(route: string): string[] {
    return [this.i18nStore.routeRoot, route]
  }

  private finishRetest(
    download: number,
    upload: number,
    latency: number,
    testUuid: string,
  ): void {
    const classifications = [
      ClassificationService.I.classify(
        download * 1000,
        THRESHOLD_DOWNLOAD,
        "biggerBetter",
      ),
      ClassificationService.I.classify(
        upload * 1000,
        THRESHOLD_UPLOAD,
        "biggerBetter",
      ),
      ClassificationService.I.classify(
        latency,
        THRESHOLD_PING,
        "smallerBetter",
      ),
    ]
    const fallbackQoeScore =
      classifications.reduce((sum, value) => sum + value, 0) /
      classifications.length
    const score = this.scorePercentage(fallbackQoeScore)
    const label = this.scoreLabel(fallbackQoeScore)
    const color = this.qualityColor(label)

    this.progress.set(100)
    this.testPhase.set("Test complete")
    this.quality.set({
      score,
      label,
      color,
      bgColor: `color-mix(in srgb, ${color} 12%, transparent)`,
      updatedAgo: "just now",
      download: Math.max(0, Math.round(download * 10) / 10),
      upload: Math.max(0, Math.round(upload * 10) / 10),
      latency: Math.max(0, Math.round(latency * 10) / 10),
    })
    this.activityRatings.set([])
    this.hasTestResults.set(true)
    this.testStore.isRunning.set(false)
    void this.loadCompletedTestQoe(testUuid)
    setTimeout(() => this.isRetesting.set(false), 500)
  }

  private async loadCompletedTestQoe(testUuid: string | null | undefined): Promise<void> {
    const requestVersion = ++this.qoeRequestVersion
    if (!testUuid) return

    for (let attempt = 0; attempt < 3; attempt++) {
      if (requestVersion !== this.qoeRequestVersion) return
      try {
        const result = await this.historyRepository.getResult({
          testUuid,
          openTestUuid: null,
        })
        if (requestVersion !== this.qoeRequestVersion) return
        if (result?.qoe_classification?.length || result?.qoe_score != null) {
          this.applyQoeResult(result.qoe_classification, result.qoe_score)
          return
        }
      } catch {
        // QoE processing can briefly lag behind result submission.
      }

      await new Promise((resolve) => setTimeout(resolve, (attempt + 1) * 750))
    }
  }

  private loadLatestInsight(): void {
    const clientId = globalThis.localStorage?.getItem(UUID)
    if (!clientId) return
    const snapshot = this.insightService.getPersonalSnapshot(clientId, 1)
    if (snapshot) {
      this.recentTests.set(snapshot.tests)
      if (snapshot.tests[0]) this.setQualityFromInsight(snapshot.tests[0])
    }
    this.recentTestsLoading.set(!snapshot)
    this.recentTestsError.set(false)
    this.insightService
      .getPersonal(clientId, 1, 0)
      .pipe(
        catchError(() => {
          this.recentTestsError.set(true)
          return of(null)
        }),
        finalize(() => this.recentTestsLoading.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((response) => {
        if (!response) return
        this.recentTests.set(response.tests)
        const latest = response.tests[0]
        if (latest) this.setQualityFromInsight(latest)
        else this.hasTestResults.set(false)
      })
  }

  private setQualityFromInsight(test: IPersonalInsightTest): void {
    const label = this.scoreLabel(test.qoe_score)
    const color = this.qualityColor(label)
    const score = this.scorePercentage(test.qoe_score)
    this.quality.set({
      score,
      label,
      color,
      bgColor: `color-mix(in srgb, ${color} 12%, transparent)`,
      updatedAgo: test.relative_timestamp,
      download: test.download_speed ?? 0,
      upload: test.upload_speed ?? 0,
      latency: test.ping ?? null,
    })
    this.activityRatings.set([])
    this.hasTestResults.set(true)
    void this.loadCompletedTestQoe(test.test_uuid)
  }

  private applyQoeResult(
    services: IQoeItem[] | null | undefined,
    score: number | null | undefined,
  ): void {
    const validServices = this.validQoeServices(services)
    if (!validServices.length && score == null) return

    const qoeScore =
      score ??
      validServices.reduce((sum, service) => sum + service.classification, 0) /
        validServices.length
    const label = this.scoreLabel(qoeScore)
    const color = this.qualityColor(label)

    this.quality.update((current) => ({
      ...current,
      score: this.scorePercentage(qoeScore),
      label,
      color,
      bgColor: `color-mix(in srgb, ${color} 12%, transparent)`,
    }))
    this.activityRatings.set(this.qoeActivityRatings(validServices))
  }

  private qoeActivityRatings(
    services: IQoeItem[] | null | undefined,
  ): ActivityRating[] {
    return this.validQoeServices(services)
      .sort((a, b) => b.quality - a.quality)
      .slice(0, 2)
      .map((service) => {
        const rating = this.scoreLabel(service.classification)
        return {
          activity: this.qoeCategoryLabel(service.category),
          rating,
          color: this.qualityColor(rating),
        }
      })
  }

  private validQoeServices(
    services: IQoeItem[] | null | undefined,
  ): IQoeItem[] {
    if (!Array.isArray(services)) return []
    return services.filter(
      (service) =>
        service != null &&
        typeof service.category === "string" &&
        Number.isFinite(service.classification) &&
        Number.isFinite(service.quality),
    )
  }

  private qoeCategoryLabel(category: string): string {
    return category
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")
  }

  private scoreLabel(score: number | null | undefined): QualityLabel {
    if (score == null || !Number.isFinite(score)) return "Low data"
    if (score >= 3.5) return "Excellent"
    if (score >= 2.5) return "Good"
    if (score >= 1.5) return "Fair"
    return "Poor"
  }

  private scorePercentage(score: number | null | undefined): number {
    if (score == null || !Number.isFinite(score)) return 0
    return Math.max(0, Math.min(100, Math.round((score / 4) * 100)))
  }

  private failRetest(message: string): void {
    this.testService.stopUpdates()
    this.testStore.isRunning.set(false)
    this.isRetesting.set(false)
    this.progress.set(0)
    this.testError.set(message)
  }

  private phaseLabel(phase: EMeasurementStatus): string {
    switch (phase) {
      case EMeasurementStatus.PING:
        return "Measuring latency…"
      case EMeasurementStatus.INIT_DOWN:
      case EMeasurementStatus.DOWN:
        return "Measuring download speed…"
      case EMeasurementStatus.INIT_UP:
      case EMeasurementStatus.UP:
        return "Measuring upload speed…"
      case EMeasurementStatus.SUBMITTING_RESULTS:
        return "Saving results…"
      default:
        return "Connecting to the test server…"
    }
  }

  private toBestSpot(spot: ISpotRecommendation): BestSpot {
    const quality = this.qualityLabel(spot.qoeBadge)
    const category = this.categoryFilters.find(
      (item) => item.id === this.selectedCategory(),
    )
    const confidence = Math.round((spot.confidence ?? 0) * 100)
    const details = [
      `${confidence}% confidence from ${spot.sampleCount} measurement${spot.sampleCount === 1 ? "" : "s"}.`,
      spot.walkTimeMinutes != null
        ? `${spot.walkTimeMinutes} min walk away.`
        : null,
    ].filter(Boolean)

    return {
      id: spot.spotId,
      label: category?.label ?? "Internet",
      place: spot.spotName,
      description: details.join(" "),
      quality,
    }
  }

  private qualityLabel(badge: string | null): QualityLabel {
    return badge === "Excellent" || badge === "Good" || badge === "Fair" || badge === "Poor"
      ? badge
      : "Low data"
  }

  announcementIcon(item: IAnnouncement): string {
    const category = item.categories.map((value) => value.name).join(" ").toLowerCase()
    return category.includes("urgent")
      ? "warning"
      : category.includes("event")
        ? "event"
        : category.includes("maint")
          ? "build"
          : "campaign"
  }

  private qualityColor(label: QualityLabel): string {
    return label === "Excellent"
      ? "var(--color-excellent)"
      : label === "Good"
        ? "var(--color-good)"
        : label === "Fair"
          ? "var(--color-warning)"
          : label === "Poor"
            ? "var(--color-error)"
            : "var(--color-text-disabled)"
  }

}
